import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import { localStorage } from '@change-corgi/core/storage';

import { useStartAPetitionPageData, useStartAPetitionPageState } from 'src/app/pages/startAPetitionV2/pageContext';
import { deleteDraftAuthor } from 'src/app/shared/api/petitionDraft';
import { useLoginState } from 'src/app/shared/hooks/session';
import { isLoaded, isLoading } from 'src/app/shared/utils/async';

import { createPublishedPetition } from './api/createPublishedPetition';
import { usePrePublishTracking } from './tracking';

type LoginState = ReturnType<typeof useLoginState>;
type Draft = NonNullable<Exclude<ReturnType<typeof useStartAPetitionPageState>['data']['draft'], 'input' | 'metadata'>>;
type PublishedPetition = Awaited<ReturnType<typeof createPublishedPetition>>;

type UsePrePublishReturn = ModelHookResult<
	{ isLoading: boolean; loginState: LoginState; error: Error | null },
	{ back: () => void; submit: () => void }
>;

export function usePrePublish(): UsePrePublishReturn {
	const { data, actions } = useStartAPetitionPageState();
	const { data: pageData } = useStartAPetitionPageData();

	const isKickstartExperimentEnabled = pageData.featureConfigs.petitionKickstartToggle;

	const utilityContext = useUtilityContext();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const track = useTracking();
	const loginState = useLoginState();
	const { pathname, search } = useLocation();

	const tracking = usePrePublishTracking();

	const {
		mutate: publishPetition,
		error: publishError,
		...mutationState
	} = useMutation({
		mutationFn: async (draft: Draft) =>
			createPublishedPetition({ petition: draft, isKickstartExperimentEnabled }, utilityContext),
		onMutate: () => {
			void track('sap_prepublish_submit');
		},
		onSuccess: (petition: PublishedPetition, draft: Draft) => {
			tracking.trackPrePublishActivatePetition({ petition, draft });
			tracking.trackConversion({ petition });

			deleteDraftAuthor({ storage: localStorage, queryClient });
			navigate(`/p/${petition.slug ?? petition.id}/starter-onboarding/welcome`);
		},
		onError: (error) => {
			const err = error instanceof Error ? error : new Error('SAP: Failed to save petition');
			void track('activate_petition_error', { context: 'prepublish', error_type: err.message });
		},
	});

	const back = useCallback(() => {
		void track('sap_prepublish_back');

		actions.send({ type: 'back' });
	}, [actions, track]);

	const submit = useCallback(() => {
		if (loginState === 'AUTHENTICATED' && data.draft) {
			publishPetition(data.draft);
		} else {
			navigate(`/login_or_join?redirectTo=${encodeURIComponent(`${pathname}${search}`)}&user_flow=sap`);
		}
	}, [data.draft, publishPetition, navigate, loginState, pathname, search]);

	return {
		data: {
			isLoading: isLoading(mutationState) || isLoaded(mutationState),
			loginState,
			error: publishError,
		},
		actions: {
			back,
			submit,
		},
	};
}
